'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  Shield,
  FileCheck,
  Server,
  Cookie,
  Brain,
  Lock,
  CreditCard,
  Heart,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Info,
  ArrowRight,
  Building2,
  FolderPlus,
  Upload,
  Scan,
  FileText,
  Globe,
  Users,
  Database,
  Key,
  Eye,
  Clock,
  BookOpen,
  Target,
  Zap,
} from 'lucide-react';

interface Framework {
  id: string;
  name: string;
  fullName: string;
  icon: React.ReactNode;
  description: string;
  applicableTo: string[];
  keyRequirements: {
    title: string;
    description: string;
    howWeCheck: string;
    whatYouProvide: string[];
  }[];
  gettingStarted: string[];
  documentationUrl?: string;
  enforcementDate?: string;
  penalties?: string;
}

const frameworks: Framework[] = [
  {
    id: 'gdpr',
    name: 'GDPR',
    fullName: 'EU General Data Protection Regulation',
    icon: <Shield className="h-6 w-6" />,
    description: 'The GDPR protects EU citizens\' personal data and privacy. It applies to any organization processing personal data of EU residents, regardless of where the organization is based.',
    applicableTo: [
      'Any company processing personal data of EU residents',
      'Companies with establishments in the EU',
      'Online services targeting EU customers',
      'Companies monitoring behavior of EU residents',
    ],
    enforcementDate: 'May 25, 2018',
    penalties: 'Up to €20 million or 4% of annual global turnover',
    keyRequirements: [
      {
        title: 'Data Processing Legal Basis',
        description: 'Every processing of personal data must have a valid legal basis (consent, contract, legal obligation, etc.)',
        howWeCheck: 'We analyze your assets and services to identify which ones process personal data and verify you have documented the legal basis.',
        whatYouProvide: [
          'List of services/assets that process personal data',
          'Legal basis for each data processing activity',
          'Consent mechanisms used (if applicable)',
        ],
      },
      {
        title: 'Data Subject Rights',
        description: 'Individuals have rights to access, rectify, erase, and port their data',
        howWeCheck: 'We check if your systems support data export, deletion, and access request fulfillment.',
        whatYouProvide: [
          'Data export capabilities per system',
          'Deletion procedures and retention periods',
          'Process for handling subject access requests',
        ],
      },
      {
        title: 'Data Protection by Design',
        description: 'Privacy must be built into systems from the ground up',
        howWeCheck: 'We analyze encryption, access controls, and security measures of your assets.',
        whatYouProvide: [
          'Encryption status (at rest and in transit)',
          'Access control mechanisms (MFA, RBAC)',
          'Data minimization practices',
        ],
      },
      {
        title: 'Cross-Border Transfers',
        description: 'Personal data transfers outside the EU require adequate safeguards',
        howWeCheck: 'We identify services with data storage outside the EU and verify transfer mechanisms.',
        whatYouProvide: [
          'Data location for each service',
          'Transfer mechanisms (SCCs, adequacy decisions)',
          'Sub-processor locations',
        ],
      },
      {
        title: 'Vendor Management',
        description: 'Data processors must have Data Processing Agreements (DPAs) in place',
        howWeCheck: 'We track your vendors and verify DPA status for those processing personal data.',
        whatYouProvide: [
          'List of vendors/sub-processors',
          'DPA status for each vendor',
          'Vendor security certifications',
        ],
      },
    ],
    gettingStarted: [
      'Create an organization representing your company',
      'Add your software services and vendors as assets',
      'For each asset, specify: data location, encryption, PII handling',
      'Run a compliance scan to identify gaps',
      'Review the GDPR compliance report and recommendations',
    ],
  },
  {
    id: 'nis2',
    name: 'NIS2',
    fullName: 'Network and Information Security Directive 2',
    icon: <Server className="h-6 w-6" />,
    description: 'NIS2 is the EU\'s updated cybersecurity directive requiring essential and important entities to implement robust security measures and report incidents.',
    applicableTo: [
      'Energy, transport, banking, health sectors',
      'Digital infrastructure providers',
      'ICT service management (B2B)',
      'Public administration',
      'Companies with 50+ employees or €10M+ turnover in covered sectors',
    ],
    enforcementDate: 'October 17, 2024',
    penalties: 'Up to €10 million or 2% of annual turnover',
    keyRequirements: [
      {
        title: 'Risk Management Measures',
        description: 'Implement appropriate technical and organizational security measures',
        howWeCheck: 'We verify security controls including encryption, access management, and monitoring across your assets.',
        whatYouProvide: [
          'Security policies and procedures',
          'Encryption implementation details',
          'Access control configuration (MFA, RBAC, PAM)',
        ],
      },
      {
        title: 'Incident Response',
        description: 'Detect, respond to, and report security incidents within 24-72 hours',
        howWeCheck: 'We check for incident response plans, monitoring capabilities, and reporting procedures.',
        whatYouProvide: [
          'Incident response plan status',
          'Security monitoring tools in use',
          'Incident classification scheme',
        ],
      },
      {
        title: 'Supply Chain Security',
        description: 'Assess and manage security risks in your supply chain',
        howWeCheck: 'We analyze your vendor ecosystem and their security posture.',
        whatYouProvide: [
          'Vendor list with criticality ratings',
          'Vendor security assessments',
          'Supply chain risk documentation',
        ],
      },
      {
        title: 'Business Continuity',
        description: 'Ensure operational continuity through backup and recovery measures',
        howWeCheck: 'We verify backup configurations, RTO/RPO targets, and disaster recovery capabilities.',
        whatYouProvide: [
          'Backup frequency and retention',
          'Recovery time objectives (RTO)',
          'Recovery point objectives (RPO)',
          'DR test results',
        ],
      },
    ],
    gettingStarted: [
      'Create an organization and classify it by sector',
      'Add all critical IT systems and infrastructure as assets',
      'Document security controls for each asset',
      'Add incident response and business continuity information',
      'Run NIS2 compliance assessment',
    ],
  },
  {
    id: 'dora',
    name: 'DORA',
    fullName: 'Digital Operational Resilience Act',
    icon: <Zap className="h-6 w-6" />,
    description: 'DORA ensures that financial entities can withstand, respond to, and recover from ICT-related disruptions and threats.',
    applicableTo: [
      'Banks and credit institutions',
      'Investment firms',
      'Insurance companies',
      'Payment service providers',
      'Crypto-asset service providers',
      'ICT third-party service providers to financial entities',
    ],
    enforcementDate: 'January 17, 2025',
    penalties: 'Up to €10 million or 5% of annual turnover for critical violations',
    keyRequirements: [
      {
        title: 'ICT Risk Management',
        description: 'Establish comprehensive ICT risk management framework',
        howWeCheck: 'We verify the existence and documentation of ICT risk policies, asset classification, and risk assessments.',
        whatYouProvide: [
          'ICT risk management framework status',
          'Asset criticality classification',
          'Risk assessment documentation',
        ],
      },
      {
        title: 'ICT Third-Party Risk',
        description: 'Manage risks from ICT service providers with formal contracts and oversight',
        howWeCheck: 'We track your ICT vendors, contract terms, concentration risk, and exit strategies.',
        whatYouProvide: [
          'ICT third-party register',
          'Vendor exit strategies',
          'Concentration risk assessment',
          'Service level agreements',
        ],
      },
      {
        title: 'Resilience Testing',
        description: 'Regularly test ICT systems through vulnerability assessments and penetration testing',
        howWeCheck: 'We verify testing schedules, results, and remediation tracking.',
        whatYouProvide: [
          'Penetration testing frequency and results',
          'Vulnerability scanning schedule',
          'Threat-led penetration testing (TLPT) status',
        ],
      },
      {
        title: 'Incident Management',
        description: 'Classify, report, and manage ICT-related incidents',
        howWeCheck: 'We check incident classification schemes, reporting capabilities, and historical incident data.',
        whatYouProvide: [
          'Incident classification scheme',
          'Incident reporting procedures',
          'Major incident history',
        ],
      },
    ],
    gettingStarted: [
      'Create an organization tagged as a financial entity',
      'Add all ICT systems and third-party providers',
      'Document criticality and resilience measures for each',
      'Add vendor contract and exit strategy information',
      'Run DORA compliance assessment',
    ],
  },
  {
    id: 'eprivacy',
    name: 'ePrivacy',
    fullName: 'ePrivacy Directive (Cookie Law)',
    icon: <Cookie className="h-6 w-6" />,
    description: 'The ePrivacy Directive regulates electronic communications, including cookies, tracking technologies, and marketing communications.',
    applicableTo: [
      'Any website or app targeting EU users',
      'Email marketers',
      'Companies using cookies or tracking',
      'Telecommunication providers',
    ],
    enforcementDate: '2002 (updated 2009)',
    penalties: 'Varies by EU member state, typically €50,000 - €500,000+',
    keyRequirements: [
      {
        title: 'Cookie Consent',
        description: 'Obtain informed consent before placing non-essential cookies',
        howWeCheck: 'We scan your websites for cookies, consent mechanisms, and pre-consent behavior.',
        whatYouProvide: [
          'Website URLs to scan',
          'Cookie consent implementation details',
          'Cookie policy documentation',
        ],
      },
      {
        title: 'Tracking Transparency',
        description: 'Clearly inform users about all tracking technologies used',
        howWeCheck: 'We identify all tracking pixels, analytics, and third-party scripts.',
        whatYouProvide: [
          'List of tracking technologies used',
          'Third-party integrations',
          'Privacy policy with tracking disclosure',
        ],
      },
      {
        title: 'Marketing Communications',
        description: 'Obtain opt-in consent for marketing emails and messages',
        howWeCheck: 'We verify consent collection for marketing and unsubscribe mechanisms.',
        whatYouProvide: [
          'Marketing consent collection methods',
          'Unsubscribe mechanism implementation',
          'Marketing list management practices',
        ],
      },
    ],
    gettingStarted: [
      'Add your websites as assets',
      'Run a website scan to detect cookies and trackers',
      'Document your consent mechanism',
      'Review detected third-party cookies',
      'Generate ePrivacy compliance report',
    ],
  },
  {
    id: 'ai-act',
    name: 'AI Act',
    fullName: 'EU Artificial Intelligence Act',
    icon: <Brain className="h-6 w-6" />,
    description: 'The AI Act establishes rules for AI systems based on their risk level, from minimal risk to prohibited practices.',
    applicableTo: [
      'AI system providers and deployers in the EU',
      'Companies placing AI systems on the EU market',
      'Companies using AI for decision-making',
      'AI systems affecting EU residents',
    ],
    enforcementDate: 'Phased: August 2025 - August 2027',
    penalties: 'Up to €35 million or 7% of annual turnover',
    keyRequirements: [
      {
        title: 'AI System Classification',
        description: 'Classify AI systems by risk level: prohibited, high-risk, limited, or minimal',
        howWeCheck: 'We analyze your AI systems against Annex III categories to determine risk classification.',
        whatYouProvide: [
          'List of AI/ML systems in use',
          'Purpose and use case for each',
          'Whether systems make autonomous decisions',
        ],
      },
      {
        title: 'High-Risk Requirements',
        description: 'High-risk AI systems require conformity assessment, documentation, and human oversight',
        howWeCheck: 'We verify documentation, testing, and oversight measures for high-risk systems.',
        whatYouProvide: [
          'AI system documentation',
          'Training data information',
          'Human oversight procedures',
          'Testing and validation results',
        ],
      },
      {
        title: 'Transparency',
        description: 'Users must be informed when interacting with AI systems',
        howWeCheck: 'We check for AI disclosure notices and transparency measures.',
        whatYouProvide: [
          'AI interaction disclosure methods',
          'Explainability documentation',
          'User notification procedures',
        ],
      },
    ],
    gettingStarted: [
      'Inventory all AI/ML systems and tools',
      'Add each AI system as an asset with detailed information',
      'Document the purpose and decision-making scope',
      'Run AI Act classification assessment',
      'Review requirements based on risk level',
    ],
  },
  {
    id: 'iso27001',
    name: 'ISO 27001',
    fullName: 'Information Security Management System',
    icon: <Lock className="h-6 w-6" />,
    description: 'ISO 27001 is an international standard for information security management systems (ISMS), providing a framework for managing security risks.',
    applicableTo: [
      'Any organization seeking to protect information assets',
      'Companies requiring security certification',
      'Organizations handling sensitive data',
      'Vendors to security-conscious customers',
    ],
    penalties: 'No direct regulatory penalties, but loss of certification affects business',
    keyRequirements: [
      {
        title: 'Asset Management (A.8)',
        description: 'Identify and classify information assets with appropriate protection',
        howWeCheck: 'We verify asset inventory, classification, and ownership documentation.',
        whatYouProvide: [
          'Complete asset inventory',
          'Data classification levels',
          'Asset owners and custodians',
        ],
      },
      {
        title: 'Access Control (A.9)',
        description: 'Restrict access to information based on business needs',
        howWeCheck: 'We analyze access control mechanisms across your systems.',
        whatYouProvide: [
          'Access control policies',
          'MFA implementation status',
          'User access review procedures',
        ],
      },
      {
        title: 'Cryptography (A.10)',
        description: 'Use encryption to protect information confidentiality and integrity',
        howWeCheck: 'We verify encryption implementation for data at rest and in transit.',
        whatYouProvide: [
          'Encryption standards used',
          'Key management procedures',
          'Certificate management',
        ],
      },
      {
        title: 'Operations Security (A.12)',
        description: 'Ensure correct and secure operations of information systems',
        howWeCheck: 'We check logging, monitoring, and vulnerability management.',
        whatYouProvide: [
          'Logging and monitoring configuration',
          'Change management procedures',
          'Vulnerability management process',
        ],
      },
      {
        title: 'Supplier Relationships (A.15)',
        description: 'Manage security in supplier relationships',
        howWeCheck: 'We track vendor security assessments and contractual requirements.',
        whatYouProvide: [
          'Vendor security requirements',
          'Supplier security assessments',
          'Security clauses in contracts',
        ],
      },
    ],
    gettingStarted: [
      'Create a comprehensive asset inventory',
      'Classify assets by data sensitivity',
      'Document security controls for each asset',
      'Add vendor security certifications',
      'Run ISO 27001 gap analysis',
    ],
  },
  {
    id: 'soc2',
    name: 'SOC 2',
    fullName: 'Service Organization Control 2',
    icon: <FileCheck className="h-6 w-6" />,
    description: 'SOC 2 is an auditing standard for service organizations, verifying controls for security, availability, processing integrity, confidentiality, and privacy.',
    applicableTo: [
      'SaaS providers',
      'Cloud service providers',
      'Data hosting companies',
      'Any service organization handling customer data',
    ],
    penalties: 'No direct penalties, but audit failures affect customer trust',
    keyRequirements: [
      {
        title: 'Security (CC)',
        description: 'Protect against unauthorized access',
        howWeCheck: 'We verify access controls, encryption, and security monitoring.',
        whatYouProvide: [
          'Access control implementation',
          'Security monitoring tools',
          'Penetration testing results',
        ],
      },
      {
        title: 'Availability',
        description: 'Ensure systems are available for operation and use',
        howWeCheck: 'We check uptime monitoring, backup, and disaster recovery.',
        whatYouProvide: [
          'Uptime SLAs and monitoring',
          'Backup and recovery procedures',
          'Disaster recovery plan',
        ],
      },
      {
        title: 'Confidentiality',
        description: 'Protect confidential information',
        howWeCheck: 'We verify data classification and protection measures.',
        whatYouProvide: [
          'Data classification scheme',
          'Confidential data handling procedures',
          'Data retention and disposal',
        ],
      },
      {
        title: 'Processing Integrity',
        description: 'Ensure system processing is complete and accurate',
        howWeCheck: 'We check input validation, error handling, and quality assurance.',
        whatYouProvide: [
          'Data validation procedures',
          'Quality assurance processes',
          'Error handling documentation',
        ],
      },
    ],
    gettingStarted: [
      'Document all systems in scope for SOC 2',
      'Add security controls for each system',
      'Document uptime and availability measures',
      'Add evidence of control implementation',
      'Run SOC 2 readiness assessment',
    ],
  },
  {
    id: 'hipaa',
    name: 'HIPAA',
    fullName: 'Health Insurance Portability and Accountability Act',
    icon: <Heart className="h-6 w-6" />,
    description: 'HIPAA protects sensitive patient health information (PHI) from being disclosed without consent.',
    applicableTo: [
      'Healthcare providers',
      'Health plans and insurers',
      'Healthcare clearinghouses',
      'Business associates handling PHI',
      'Health tech companies',
    ],
    penalties: 'Up to $1.5 million per violation category per year',
    keyRequirements: [
      {
        title: 'PHI Safeguards',
        description: 'Implement administrative, physical, and technical safeguards for PHI',
        howWeCheck: 'We verify encryption, access controls, and audit logging for PHI systems.',
        whatYouProvide: [
          'Systems containing PHI',
          'Encryption status for PHI',
          'Access control implementation',
          'Audit logging configuration',
        ],
      },
      {
        title: 'Access Controls',
        description: 'Implement unique user identification and automatic logoff',
        howWeCheck: 'We check for unique user IDs, role-based access, and session management.',
        whatYouProvide: [
          'User authentication methods',
          'Role-based access configuration',
          'Session timeout settings',
        ],
      },
      {
        title: 'Business Associate Agreements',
        description: 'BAAs required with all vendors handling PHI',
        howWeCheck: 'We track BAA status for all vendors with PHI access.',
        whatYouProvide: [
          'List of business associates',
          'BAA status for each',
          'PHI handling documentation',
        ],
      },
      {
        title: 'Breach Notification',
        description: 'Report breaches within 60 days',
        howWeCheck: 'We verify incident response and breach notification procedures.',
        whatYouProvide: [
          'Breach notification procedures',
          'Incident response plan',
          'Breach risk assessment process',
        ],
      },
    ],
    gettingStarted: [
      'Identify all systems containing PHI',
      'Add PHI systems as assets with health data flag',
      'Document safeguards for each system',
      'Track business associate agreements',
      'Run HIPAA compliance assessment',
    ],
  },
  {
    id: 'pci-dss',
    name: 'PCI DSS',
    fullName: 'Payment Card Industry Data Security Standard',
    icon: <CreditCard className="h-6 w-6" />,
    description: 'PCI DSS ensures that companies handling credit card data maintain a secure environment to protect cardholder information.',
    applicableTo: [
      'Merchants accepting card payments',
      'Payment processors',
      'Service providers handling card data',
      'Any entity storing, processing, or transmitting cardholder data',
    ],
    penalties: 'Fines from $5,000 to $100,000 per month until compliance',
    keyRequirements: [
      {
        title: 'Cardholder Data Protection',
        description: 'Protect stored cardholder data and encrypt transmission',
        howWeCheck: 'We verify encryption, tokenization, and data minimization practices.',
        whatYouProvide: [
          'Systems storing card data',
          'Encryption methods used',
          'Tokenization implementation',
          'Data retention limits',
        ],
      },
      {
        title: 'Network Security',
        description: 'Install and maintain network security controls',
        howWeCheck: 'We check firewall rules, network segmentation, and secure configurations.',
        whatYouProvide: [
          'Network diagram',
          'Firewall configurations',
          'Network segmentation details',
        ],
      },
      {
        title: 'Access Control',
        description: 'Restrict access to cardholder data by business need',
        howWeCheck: 'We verify least privilege access and unique user identification.',
        whatYouProvide: [
          'Access control policies',
          'User access reviews',
          'Privileged access management',
        ],
      },
      {
        title: 'Vulnerability Management',
        description: 'Regularly scan for vulnerabilities and apply patches',
        howWeCheck: 'We check scanning schedules, patch management, and remediation tracking.',
        whatYouProvide: [
          'Vulnerability scanning frequency',
          'Patch management procedures',
          'Penetration testing results',
        ],
      },
    ],
    gettingStarted: [
      'Map your cardholder data environment (CDE)',
      'Add all systems in CDE as assets',
      'Document security controls per PCI DSS requirements',
      'Track vulnerability scanning and patching',
      'Run PCI DSS compliance assessment',
    ],
  },
];

const steps = [
  {
    icon: <Building2 className="h-8 w-8" />,
    title: '1. Create Organization',
    description: 'Set up your company profile with sector classification',
    link: '/organizations/new',
  },
  {
    icon: <FolderPlus className="h-8 w-8" />,
    title: '2. Create Project',
    description: 'Add a project or product to assess',
    link: '/projects/new',
  },
  {
    icon: <Upload className="h-8 w-8" />,
    title: '3. Add Assets',
    description: 'Import your software dependencies or add vendors manually',
  },
  {
    icon: <Scan className="h-8 w-8" />,
    title: '4. Run Scan',
    description: 'Analyze your assets against compliance frameworks',
  },
  {
    icon: <FileText className="h-8 w-8" />,
    title: '5. Review Report',
    description: 'Get actionable recommendations and export reports',
  },
];

export default function ComplianceGuidePage() {
  const [expandedFramework, setExpandedFramework] = useState<string | null>('gdpr');
  const [selectedTab, setSelectedTab] = useState<'overview' | 'requirements' | 'getting-started'>('overview');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Compliance Documentation
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Learn how to assess your organization against GDPR, NIS2, DORA, and 6 other compliance frameworks.
              Understand what each check requires and how to provide the necessary information.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Getting Started Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            How to Run a Compliance Check
          </h2>
          <div className="grid md:grid-cols-5 gap-4">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-purple-600">
                    {step.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                  {step.link && (
                    <Link
                      href={step.link}
                      className="mt-4 inline-flex items-center text-purple-600 text-sm font-medium hover:text-purple-700"
                    >
                      Start <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ChevronRight className="h-6 w-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-12">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              href="/organizations/new"
              className="flex items-center gap-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors"
            >
              <Building2 className="h-5 w-5 text-purple-600" />
              <span className="font-medium text-purple-900">Create Organization</span>
            </Link>
            <Link
              href="/projects/new"
              className="flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
            >
              <FolderPlus className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-900">New Project</span>
            </Link>
            <Link
              href="/dashboard"
              className="flex items-center gap-3 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors"
            >
              <Scan className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-900">Run Scan</span>
            </Link>
            <Link
              href="/apps"
              className="flex items-center gap-3 p-4 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors"
            >
              <Globe className="h-5 w-5 text-amber-600" />
              <span className="font-medium text-amber-900">Find Alternatives</span>
            </Link>
          </div>
        </div>

        {/* Framework Documentation */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Framework Documentation
        </h2>
        <p className="text-gray-600 mb-8">
          Click on any framework below to learn what we check, what information you need to provide, and how to get started.
        </p>

        <div className="space-y-4">
          {frameworks.map((framework) => (
            <div key={framework.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Framework Header */}
              <button
                onClick={() => setExpandedFramework(expandedFramework === framework.id ? null : framework.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    framework.id === 'gdpr' ? 'bg-blue-100 text-blue-600' :
                    framework.id === 'nis2' ? 'bg-green-100 text-green-600' :
                    framework.id === 'dora' ? 'bg-purple-100 text-purple-600' :
                    framework.id === 'eprivacy' ? 'bg-amber-100 text-amber-600' :
                    framework.id === 'ai-act' ? 'bg-pink-100 text-pink-600' :
                    framework.id === 'iso27001' ? 'bg-indigo-100 text-indigo-600' :
                    framework.id === 'soc2' ? 'bg-cyan-100 text-cyan-600' :
                    framework.id === 'hipaa' ? 'bg-red-100 text-red-600' :
                    'bg-orange-100 text-orange-600'
                  }`}>
                    {framework.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900 text-lg">{framework.name}</h3>
                    <p className="text-sm text-gray-500">{framework.fullName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {framework.enforcementDate && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="h-4 w-4" />
                      {framework.enforcementDate}
                    </span>
                  )}
                  <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
                    expandedFramework === framework.id ? 'rotate-180' : ''
                  }`} />
                </div>
              </button>

              {/* Expanded Content */}
              {expandedFramework === framework.id && (
                <div className="border-t border-gray-200">
                  {/* Tabs */}
                  <div className="flex border-b border-gray-200">
                    {(['overview', 'requirements', 'getting-started'] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setSelectedTab(tab)}
                        className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                          selectedTab === tab
                            ? 'border-purple-600 text-purple-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {tab === 'overview' && 'Overview'}
                        {tab === 'requirements' && 'Requirements & Checks'}
                        {tab === 'getting-started' && 'Getting Started'}
                      </button>
                    ))}
                  </div>

                  <div className="p-6">
                    {/* Overview Tab */}
                    {selectedTab === 'overview' && (
                      <div className="space-y-6">
                        <p className="text-gray-600">{framework.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <Users className="h-5 w-5 text-purple-600" />
                              Who Must Comply
                            </h4>
                            <ul className="space-y-2">
                              {framework.applicableTo.map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="space-y-4">
                            {framework.enforcementDate && (
                              <div className="bg-blue-50 rounded-xl p-4">
                                <h4 className="font-semibold text-blue-900 mb-1 flex items-center gap-2">
                                  <Clock className="h-5 w-5" />
                                  Enforcement Date
                                </h4>
                                <p className="text-blue-700">{framework.enforcementDate}</p>
                              </div>
                            )}
                            {framework.penalties && (
                              <div className="bg-red-50 rounded-xl p-4">
                                <h4 className="font-semibold text-red-900 mb-1 flex items-center gap-2">
                                  <AlertTriangle className="h-5 w-5" />
                                  Penalties
                                </h4>
                                <p className="text-red-700">{framework.penalties}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Requirements Tab */}
                    {selectedTab === 'requirements' && (
                      <div className="space-y-6">
                        {framework.keyRequirements.map((req, index) => (
                          <div key={index} className="border border-gray-200 rounded-xl p-5">
                            <h4 className="font-semibold text-gray-900 mb-2">{req.title}</h4>
                            <p className="text-gray-600 text-sm mb-4">{req.description}</p>
                            
                            <div className="grid md:grid-cols-2 gap-4">
                              <div className="bg-purple-50 rounded-lg p-4">
                                <h5 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                                  <Eye className="h-4 w-4" />
                                  How We Check
                                </h5>
                                <p className="text-sm text-purple-700">{req.howWeCheck}</p>
                              </div>
                              
                              <div className="bg-blue-50 rounded-lg p-4">
                                <h5 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                                  <Database className="h-4 w-4" />
                                  What You Provide
                                </h5>
                                <ul className="space-y-1">
                                  {req.whatYouProvide.map((item, i) => (
                                    <li key={i} className="text-sm text-blue-700 flex items-start gap-1">
                                      <span className="text-blue-400">•</span>
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Getting Started Tab */}
                    {selectedTab === 'getting-started' && (
                      <div>
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 mb-6">
                          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Target className="h-5 w-5 text-purple-600" />
                            Steps to Assess {framework.name} Compliance
                          </h4>
                          <ol className="space-y-3">
                            {framework.gettingStarted.map((step, index) => (
                              <li key={index} className="flex items-start gap-3">
                                <span className="w-6 h-6 bg-purple-600 text-white text-sm font-semibold rounded-full flex items-center justify-center shrink-0">
                                  {index + 1}
                                </span>
                                <span className="text-gray-700">{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                        
                        <div className="flex gap-4">
                          <Link
                            href="/organizations/new"
                            className="flex-1 text-center py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                          >
                            Start {framework.name} Assessment
                          </Link>
                          <Link
                            href={`/compliance/${framework.id}`}
                            className="flex-1 text-center py-3 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium"
                          >
                            Learn More About {framework.name}
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Help Section */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-4">Need Help?</h2>
            <p className="text-indigo-100 mb-6">
              Our compliance documentation is designed to help you understand what&apos;s required.
              If you have questions about a specific framework or need assistance with your assessment,
              we&apos;re here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/docs"
                className="px-6 py-3 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors"
              >
                View Full Documentation
              </Link>
              <a
                href="mailto:support@dependra.eu"
                className="px-6 py-3 border border-white text-white rounded-lg font-medium hover:bg-white/10 transition-colors"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
